import clsx from 'clsx';
import React, { Children, useMemo } from 'react';

type ChipProps = {
  chip: ChipConfig;
  onDismiss?: (value: ChipValue) => void;
  background?: string;
};

// Maps the Grommet theme color names historically passed to `background` to their
// actual color values, since the chip is rendered as a plain element (no Grommet
// theme context) so it also works inside Headless UI / Tailwind components.
const BACKGROUND_COLORS: Record<string, string> = {
  'accent-1': '#00A3A3',
  'accent-2': '#99E0DF',
  'warning': '#FFD48A',
  'error': '#fd5157',
  'status-critical': '#FF4040',
  'light-3': '#EDEDED',
  'peach': '#FDB26B',
};

const LIGHT_TEXT_BACKGROUNDS = new Set(['error', 'status-critical']);

export const Chip = ({ chip, onDismiss, background }: ChipProps) => {
  const { label, dismissible, value } = chip;

  const backgroundColor = background ? (BACKGROUND_COLORS[background] ?? background) : undefined;
  const color = background && LIGHT_TEXT_BACKGROUNDS.has(background) ? 'white' : undefined;

  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium',
        backgroundColor ? undefined : 'bg-secondary-100 text-secondary-800'
      )}
      style={backgroundColor ? { backgroundColor, color } : undefined}
    >
      {label}
      {dismissible && (
        <button
          type="button"
          aria-label="Remove"
          onClick={() => onDismiss?.(value)}
          className="ml-0.5 inline-flex size-3.5 shrink-0 items-center justify-center rounded-full text-secondary-600 hover:bg-secondary-200 hover:text-secondary-800 focus:outline-none"
        >
          <svg fill="none" stroke="currentColor" viewBox="0 0 8 8" className="size-2">
            <path d="M1 1l6 6m0-6L1 7" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          <span className="sr-only">Remove {label}</span>
        </button>
      )}
    </span>
  );
};

export type ChipConfig = {
  label: React.ReactNode;
  value: ChipValue;
  dismissible?: boolean;
};

type ChipListProps = {
  children?: React.ReactNode;
  emptyText?: string;
};

export const ChipList = ({ children, emptyText = 'No chips' }: ChipListProps) => {
  const hasChildren = useMemo(() => Children.count(children) > 0, [children]);

  return (
    <div className="flex flex-row flex-wrap items-center gap-2">
      {hasChildren ? children : (
        <span className="text-xs text-zinc-400">{emptyText}</span>
      )}
    </div>
  );
};

export type ChipValue = string | number | readonly string[];
